/**
 * HelpCenterModal — searchable FAQ + contact entry
 * GHL-killer: human tone, zero tech jargon, instant answers.
 */

import { useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Search, MessageCircle, Mail, BookOpen } from "lucide-react";
import { FAQ_ITEMS, FAQ_CATEGORIES, type FAQItem } from "@/lib/support/faq";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

interface HelpCenterModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultCategory?: FAQItem["category"];
}

export function HelpCenterModal({ open, onOpenChange, defaultCategory }: HelpCenterModalProps) {
  const [query, setQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState<FAQItem["category"] | "all">(defaultCategory ?? "all");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return FAQ_ITEMS.filter((item) => {
      if (activeCategory !== "all" && item.category !== activeCategory) return false;
      if (!q) return true;
      return item.question.toLowerCase().includes(q) || item.answer.toLowerCase().includes(q);
    });
  }, [query, activeCategory]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-primary" />
            Hulp nodig?
          </DialogTitle>
          <DialogDescription>
            Zoek een antwoord of stel je vraag direct aan ons team.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Zoek in FAQ..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-9"
            />
          </div>

          <div className="flex flex-wrap gap-1.5">
            <Badge
              variant={activeCategory === "all" ? "default" : "outline"}
              className="cursor-pointer"
              onClick={() => setActiveCategory("all")}
            >
              Alles
            </Badge>
            {FAQ_CATEGORIES.map((cat) => (
              <Badge
                key={cat.id}
                variant={activeCategory === cat.id ? "default" : "outline"}
                className="cursor-pointer"
                onClick={() => setActiveCategory(cat.id)}
              >
                {cat.label}
              </Badge>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto -mx-6 px-6">
          {filtered.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <p className="mb-2">Geen antwoord gevonden voor "{query}".</p>
              <p className="text-sm">Mail ons direct — we reageren binnen 4 uur.</p>
            </div>
          ) : (
            <Accordion type="single" collapsible className="w-full">
              {filtered.map((item) => (
                <AccordionItem key={item.id} value={item.id}>
                  <AccordionTrigger className="text-left text-sm hover:no-underline">
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-muted-foreground leading-relaxed">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}
        </div>

        <div className="border-t pt-4 flex flex-col sm:flex-row gap-2">
          <Button variant="outline" className="flex-1 gap-2" asChild>
            <a href="mailto:support@lombrici.nl">
              <Mail className="h-4 w-4" />
              Mail support
            </a>
          </Button>
          <Button variant="outline" className="flex-1 gap-2" onClick={() => onOpenChange(false)}>
            <MessageCircle className="h-4 w-4" />
            Chat met ons
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
